/**
 * Semantic colour tokens — the ONLY colour vocabulary components should use.
 *
 * Each entry maps a CSS-variable name (emitted as `--eai-<name>`) to the antd
 * alias token it is derived from. antd's theme algorithm computes the actual
 * value for every (mode × brand) combination, so the same name is correct in
 * light, dark and every brand colour without components knowing which is active.
 *
 * CommonJS on purpose: `tailwind.config.js` (Node) requires this file too, so
 * Tailwind classes and antd tokens are generated from one list and cannot drift.
 */
const SEMANTIC_TOKENS = {
  // Surfaces
  "bg-layout": "colorBgLayout", // page background behind cards
  "bg-container": "colorBgContainer", // cards, tables, inputs
  "bg-elevated": "colorBgElevated", // modals, drawers, popovers
  "bg-spotlight": "colorBgSpotlight", // tooltips
  fill: "colorFill",
  "fill-secondary": "colorFillSecondary", // tracks, neutral chips
  "fill-tertiary": "colorFillTertiary",
  "fill-quaternary": "colorFillQuaternary",

  // Text
  text: "colorText",
  "text-secondary": "colorTextSecondary",
  "text-tertiary": "colorTextTertiary",
  "text-quaternary": "colorTextQuaternary",
  "text-disabled": "colorTextDisabled",
  "text-heading": "colorTextHeading",
  "text-inverse": "colorTextLightSolid", // text on a solid primary/status fill

  // Borders
  border: "colorBorder",
  "border-secondary": "colorBorderSecondary",

  // Brand (follows the active brand seed)
  primary: "colorPrimary",
  "primary-hover": "colorPrimaryHover",
  "primary-active": "colorPrimaryActive",
  "primary-bg": "colorPrimaryBg",
  "primary-bg-hover": "colorPrimaryBgHover",
  "primary-border": "colorPrimaryBorder",
  "primary-border-hover": "colorPrimaryBorderHover",
  "primary-text": "colorPrimaryText",
  "primary-text-hover": "colorPrimaryTextHover",
  link: "colorLink",
  "link-hover": "colorLinkHover",

  // Status (independent of brand)
  success: "colorSuccess",
  "success-bg": "colorSuccessBg",
  "success-border": "colorSuccessBorder",
  "success-text": "colorSuccessText",
  warning: "colorWarning",
  "warning-bg": "colorWarningBg",
  "warning-border": "colorWarningBorder",
  "warning-text": "colorWarningText",
  error: "colorError",
  "error-bg": "colorErrorBg",
  "error-border": "colorErrorBorder",
  "error-text": "colorErrorText",
  info: "colorInfo",
  "info-bg": "colorInfoBg",
  "info-border": "colorInfoBorder",
  "info-text": "colorInfoText",
};

/**
 * Accent — the brand's secondary colour. antd has no secondary-colour concept,
 * so the accent scale is produced by running antd's algorithm with the accent
 * seed as `colorPrimary` and reading back its primary-family tokens. Same
 * derivation, same light/dark behaviour as `primary`.
 */
const ACCENT_TOKENS = {
  accent: "colorPrimary",
  "accent-hover": "colorPrimaryHover",
  "accent-active": "colorPrimaryActive",
  "accent-bg": "colorPrimaryBg",
  "accent-bg-hover": "colorPrimaryBgHover",
  "accent-border": "colorPrimaryBorder",
  "accent-border-hover": "colorPrimaryBorderHover",
  "accent-text": "colorPrimaryText",
  "accent-text-hover": "colorPrimaryTextHover",
};

const CSS_VAR_PREFIX = "--eai-";

const cssVarName = name => `${CSS_VAR_PREFIX}${name}`;

module.exports = { SEMANTIC_TOKENS, ACCENT_TOKENS, CSS_VAR_PREFIX, cssVarName };
